import { UserProfile } from '@/types/profile'
import { MonthlyAvgDataPoint } from '@/types/otc'

/**
 * Props for DashboardStats component
 * Contains all data needed for stats cards display
 *
 * 🔴 Every figure is `number | null`, and that is deliberate. They used to be
 * plain `number`, and `MainScreen` fed them `otcStats?.x || 0` — so any failure
 * of `/orderbook/stats` rendered "$0 order book, 0 projects, 0.0% discount"
 * with the same authority as a real reading. Telling a user the marketplace is
 * empty when nothing was fetched is not a smaller error than telling them the
 * wrong number; it is the same error.
 *
 * `null` means "no figure available", and each tile renders a placeholder for
 * it. Do not re-add a `|| 0`.
 */
export interface DashboardStatsProps {
  // Profile data
  profile?: UserProfile

  // Stats data. `null` = not available (not fetched, or the fetch failed).
  totalProjects: number | null
  totalOrderbookValue: number | null
  averageDiscount: number | null
  buyOrders: number | null
  sellOrders: number | null

  /**
   * The session is not entitled to financial figures — the stats endpoint was
   * never called. Distinguishes the lock from a transient failure, which needs
   * different copy and no "continue verification" button.
   */
  financialsHidden?: boolean

  /**
   * Count of live orders from the public listing the dashboard already
   * fetched. Not financial (the same number is derivable from
   * `/orderbook/public`'s own envelope), so it stands in for the projects tile
   * when the stats endpoint is off limits — one real figure on the page beats
   * four locks.
   */
  listedOrders?: number

  // Loading states
  loading?: boolean

  // Chart data
  totalProjectsMonthlyAvg?: MonthlyAvgDataPoint[]
  orderbookMonthlyAvg?: MonthlyAvgDataPoint[]
}

/**
 * Formats monthly average data for chart display
 * Extracts last N months and marks the most recent as highlighted
 */
export const formatChartData = (
  data: MonthlyAvgDataPoint[] | undefined,
  count: number = 3
): Array<{ month: string; value: number; isHighlighted: boolean }> => {
  if (!data || data.length === 0) return []
  
  return data.slice(-count).map((item, index, arr) => {
    const date = new Date(item.date)
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const isHighlighted = index === arr.length - 1
    
    return {
      month,
      value: item.value,
      isHighlighted
    }
  })
}

